import { AxiosError } from 'axios'

import { useQuery, UseQueryOptions } from '@tanstack/react-query'

import { Country } from '@/interfaces/billing'

import APIServices from '../../api/APIServices'

export interface CountriesQueryParams {
  all_data?: boolean
  search?: string
  id?: string
}

// Create a unique, descriptive key for this query
const COUNTRIES_QUERY_KEY = 'countries'

type CountriesResponse = {
  items: Country[]
  totalItems: number
  currentPage: number
  totalPages: number
}

/**
 * Fetch countries from the API
 * Separating API call logic from the hook for better testability
 */
const fetchCountries = async (
  params?: CountriesQueryParams
): Promise<CountriesResponse> => {
  // Filter out null/undefined values to clean up the request
  const cleanParams = Object.fromEntries(
    Object.entries(params || {}).filter(([_, value]) => value != null)
  )

  const response = await APIServices.get('/invoice/public/countries', {
    params: cleanParams,
  })
  return response.data
}

/**
 * Custom hook for fetching countries
 *
 * @param params - Query parameters for filtering countries
 * @param options - TanStack Query options for customizing query behavior
 * @returns Query result object containing data, loading states, and utility functions
 */
export const useCountriesQuery = ({
  params = { all_data: true },
  options,
}: {
  params?: CountriesQueryParams
  options?: Partial<UseQueryOptions<CountriesResponse, AxiosError>>
}) => {
  // Query hook
  const queryResult = useQuery<CountriesResponse, AxiosError>({
    // Create a structured query key that includes all parameters
    queryKey: [COUNTRIES_QUERY_KEY, params],

    // Separate the API call logic from the query configuration
    queryFn: () => fetchCountries(params),

    // Default options that can be overridden
    refetchOnWindowFocus: false,
    staleTime: 10 * 60 * 1000, // 10 minutes (location data doesn't change often)

    // Apply any custom options
    ...options,
  })

  return {
    countries: queryResult.data?.items || [],
    isLoadingCountries: queryResult.isLoading,
    isErrorCountries: queryResult.isError,
    errorCountries: queryResult.error,
    refetchCountries: queryResult.refetch,
    isFetchingCountries: queryResult.isFetching,
  }
}

/**
 * Helper function to get the provinces query key
 * Useful for cache invalidation or prefetching
 */
export const getCountriesQueryKey = (params?: CountriesQueryParams) => [
  COUNTRIES_QUERY_KEY,
  params,
]
